import { AlertTriangle, Archive, Database, HardDrive, Image, RefreshCw, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { adminApi } from "./api";

interface Usage {
  totalBytes: number;
  mediaBytes: number;
  backupBytes: number;
  mediaFiles: number;
  backupFiles: number;
  latestBackupAt: string | null;
  backupCount: number;
  warningLevel: "normal" | "warning" | "high" | "critical";
  freeLimitBytes: number;
  usagePercent: number;
}

export function StoragePage() {
  const [usage, setUsage] = useState<Usage | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    setError("");
    try { setUsage(await adminApi<Usage>("/storage/r2-usage")); }
    catch (caught) { setError(caught instanceof Error ? caught.message : "Unable to load R2 usage"); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const action = async (kind: "media" | "backups") => {
    const label = kind === "media" ? "unused media" : "old backups";
    if (!window.confirm(`Scan and delete ${label}? Referenced media and the latest 3 backups are always protected.`)) return;
    setBusy(true); setMessage(""); setError("");
    try {
      const result = await adminApi<{ filesScanned: number; filesDeleted: number; bytesDeleted: number }>(kind === "media" ? "/storage/clean-unused-media" : "/storage/delete-old-backups", {
        method: "POST",
        body: JSON.stringify({ confirmation: kind === "media" ? "CONFIRM CLEAN UNUSED MEDIA" : "CONFIRM DELETE OLD BACKUPS" }),
      });
      setMessage(`Scanned ${result.filesScanned} files. Deleted ${result.filesDeleted} files (${formatBytes(result.bytesDeleted)}).`);
      await load();
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Cleanup failed"); }
    finally { setBusy(false); }
  };

  const cards = [
    ["Total used", formatBytes(usage?.totalBytes), HardDrive],
    ["Media", formatBytes(usage?.mediaBytes), Image],
    ["Backups", formatBytes(usage?.backupBytes), Database],
    ["Media files", usage?.mediaFiles ?? "—", Image],
    ["Backup files", usage?.backupFiles ?? "—", Archive],
    ["Current backups", usage?.backupCount ?? "—", Archive],
  ] as const;
  return <><div className="page-title"><div><p>ALIEXPRESS CASHBACK</p><h1>Storage / R2 Usage</h1><span>Live usage, retention controls, and safe cleanup for Cloudflare R2.</span></div></div>
    {usage && usage.warningLevel !== "normal" ? <div className={`storage-alert ${usage.warningLevel}`}><AlertTriangle size={19}/><b>{warningText(usage.warningLevel)}</b><span>{formatBytes(usage.totalBytes)} used. Keep usage below the 10 GB free allowance.</span></div> : null}
    {error ? <div className="error"><AlertTriangle size={18}/>{error}</div> : null}
    <div className="kpi-grid">{cards.map(([label, value, Icon]) => <article className="kpi" key={label}><div className="kpi-icon"><Icon size={19}/></div><span>{label}</span><strong>{value}</strong><small>{label === "Total used" ? usage ? `${usage.usagePercent}% of 10 GB` : "Waiting for live R2 data" : "Live R2 value"}</small></article>)}</div>
    <section className="panel"><div className="panel-head"><div><h2>Backup status</h2><p>Compressed database/settings backups only. Media is never copied into backups.</p></div><button className="secondary" onClick={() => void load()}><RefreshCw size={16}/>Refresh</button></div><div className="storage-facts"><div><span>Latest backup</span><b>{usage?.latestBackupAt ? new Date(usage.latestBackupAt).toLocaleString() : "No backup yet"}</b></div><div><span>Retention</span><b>Latest 3 backups</b></div><div><span>Warning thresholds</span><b>7 GB · 8 GB · 9 GB</b></div></div></section>
    <section className="panel"><div className="panel-head"><div><h2>Safe cleanup</h2><p>Database references are checked before any media object is removed.</p></div></div><div className="toolbar actions"><button className="primary" disabled={busy} onClick={() => void action("media")}><Trash2 size={16}/>Clean unused media</button><button className="secondary" disabled={busy} onClick={() => void action("backups")}><Archive size={16}/>Delete old backups</button></div>{message ? <div className="success-note">{message}</div> : null}</section>
  </>;
}

function warningText(level: Usage["warningLevel"]) {
  if (level === "critical") return "Critical";
  if (level === "high") return "High usage";
  return "Warning";
}

function formatBytes(value?: number) {
  if (value === undefined) return "—";
  if (value < 1000) return `${value} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let current = value / 1000; let index = 0;
  while (current >= 1000 && index < units.length - 1) { current /= 1000; index += 1; }
  return `${current.toFixed(current >= 100 ? 0 : current >= 10 ? 1 : 2)} ${units[index]}`;
}
